import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Box,
  ButtonBase,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import DatePickerField from "../shared/ui/HaklaiDatePickerField";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Swal from "sweetalert2";
import CalculatorIcon from "../../../layout/CalculatorIcon";
import { getHaklaiDeductionTypeOptions } from "../../services/haklaiDealService";
import {
  CATEGORIES,
  COMPACT_CURRENCY_LABELS,
  ensureHaklaiSelectedCurrencyOption,
  getHaklaiCurrencyOptions,
  MAX_MONEY_DIGITS,
  normalizeHaklaiCurrencyForDate,
  ZABAN_ATTRIBUTION_OPTIONS
} from "../shared/haklaiConstants";
import {
  HaklaiStyledHeaderCell as StyledHeaderCell,
  HaklaiStyledCell as StyledCell,
  FieldShell,
  buildFieldErrorId
} from "../shared/table/haklaiTableShared";
import useHaklaiTableFit from "../shared/table/useHaklaiTableFit";
import useHaklaiTableValidationController from "../shared/table/useHaklaiTableValidationController";
import HaklaiSectionLabelCell, {
  HAKLAI_SECTION_LABEL_COLUMN_WIDTH
} from "../shared/ui/HaklaiSectionLabelCell";
import HaklaiLockNotice from "../shared/ui/HaklaiLockNotice";
import {
  formatHaklaiWholeMoney,
  parseHaklaiClassicMoneyInput
} from "../shared/haklaiMoneyHelpers";
import {
  getHaklaiAssetGroups,
  getHaklaiComputedAcquisitionByPart,
  getHaklaiLogicalPropertyPartsData,
  getHaklaiZabanAttributionSelections,
  isHaklaiZabanAttributionRelevant
} from "../shared/haklaiFlowHelpers";
import {
  getHaklaiAcquisitionDisplayDate,
  getHaklaiSeededExpenseTypeLabel
} from "../preCalc/logic/haklaiPreCalcHelpers";
import {
  buildHaklaiExpenseValidationContext,
  buildHaklaiExpensesValidationOverlay,
  DEFAULT_HAKLAI_ZABAN_ATTRIBUTION,
  getHaklaiExpenseDepreciationSummary,
  getHaklaiExpenseValidationState
} from "./logic/haklaiExpensesHelpers";
import {
  HAKLAI_PROPERTY_DEPRECIATION_MODE,
  normalizeHaklaiDepreciationState
} from "../shared/haklaiDepreciationHelpers";
import {
  HAKLAI_DEPRECIATION_SELECTION_LIMIT_TEXT,
  HAKLAI_DEPRECIATION_SELECTION_LIMIT_TITLE,
  getHaklaiExpenseDepreciationDecision,
  HAKLAI_EXPENSE_DEPRECIATION_DECISION,
  HAKLAI_MAX_EXPENSE_DEPRECIATION_SELECTIONS_PER_PART
} from "./logic/haklaiExpenseDepreciationPolicy";
import HaklaiExpenseRow from "./components/HaklaiExpenseRow";
import { getHaklaiPropertyDepreciationUiState } from "../shared/depreciation/haklaiPropertyDepreciationUiState";
import {
  ACTION_COLUMN_WIDTH,
  AMOUNT_COLUMN_WIDTH,
  getHaklaiCurrencyColumnWidth,
  DATE_COLUMN_WIDTH,
  DATE_INPUT_WIDTH,
  DEPRECIATION_COLUMN_WIDTH,
  DEPRECIATION_RESULT_COLUMN_WIDTH,
  EXPENSES_SECTION_TRANSITION_MS,
  getHaklaiExpensesTableWidth,
  EXPENSE_TYPE_COLUMN_WIDTH,
  useHaklaiExpensesStyles,
  ZABAN_COLUMN_WIDTH
} from "./haklaiExpensesStyles";
import {
  getHaklaiExpenseDeductionsHeader,
  getHaklaiExpenseForm11EditTooltip,
  HAKLAI_EXPENSE_ACTIONS,
  HAKLAI_EXPENSE_ERRORS,
  HAKLAI_EXPENSE_LABELS,
  HAKLAI_EXPENSE_TOOLTIPS
} from "./haklaiExpenseTexts";
import HaklaiPropertyDepreciationDialog from "../shared/depreciation/HaklaiPropertyDepreciationDialog";

/**
 * Owns the Haklai expenses table section.
 *
 * Responsibilities:
 * - renders editable expense rows and their validation state
 * - coordinates expense-level add/remove behavior and table scrolling
 * - opens depreciation detail editing where expenses need Form 11 data
 *
 * Row presentation belongs in `components/`; expense derivation and policy
 * rules belong in `logic/`.
 */
const EMPTY_ROW_FIELDS = Object.freeze({});
const EMPTY_VALIDATION_STATE = Object.freeze({
  isReusableDraft: false,
  immediateInvalidFields: [],
  fieldErrors: EMPTY_ROW_FIELDS,
  isStarted: false,
  isZabanRelevant: false
});
const EMPTY_EXPENSE_MAP = Object.freeze({});
const EMPTY_EXPENSE_LIST = Object.freeze([]);
const EMPTY_VALIDATION_OVERLAY = Object.freeze({
  cellErrors: EMPTY_EXPENSE_MAP
});
const FORM_11_ACTION_COPY = { actionLabel: HAKLAI_EXPENSE_ACTIONS.enterDepreciation };

const hasPositiveDepreciationResult = (depreciation = {}) => {
  const depreciationResult = Number(depreciation?.depreciationResult);
  return Number.isFinite(depreciationResult) && depreciationResult > 0;
};

const getForm11ButtonCopy = () => FORM_11_ACTION_COPY;

const renderDepreciationButtonCopy = (classes, copy) => (
  <Box className={classes.depreciationEditButtonText}>
    <Typography component="span" className={classes.depreciationButtonActionText}>
      {copy.actionLabel}
    </Typography>
    <CalculatorIcon className={classes.depreciationButtonCalculatorIcon} />
  </Box>
);

const deferUntilAfterPaint = (callback) => {
  if (typeof window === "undefined") {
    const timeoutId = setTimeout(callback, 0);
    return () => clearTimeout(timeoutId);
  }

  if (typeof window.requestAnimationFrame === "function") {
    let timeoutId = 0;
    const frameId = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(callback, 0);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }

  const timeoutId = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timeoutId);
};

const afterNextPaint = (callback) => {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    callback();
    return;
  }
  window.requestAnimationFrame(() => window.requestAnimationFrame(callback));
};

const getExpenseFieldDomName = (expenseId, fieldName) => {
  if (!expenseId || !fieldName) return "";
  if (fieldName === "expenseType") return `expenseType_${expenseId}`;
  if (fieldName === "amount") return `expenseAmount_${expenseId}`;
  if (fieldName === "expenseCurrency") return `expenseCurrency_${expenseId}`;
  if (fieldName === "expenseDate") return `expenseDate_${expenseId}`;
  if (fieldName === "zabanAttribution") return `expenseZabanAttribution_${expenseId}`;
  if (fieldName === "depreciation") return `expenseDepreciation_${expenseId}`;
  return "";
};

const formatDepreciationTableAmount = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return "";
  const roundedValue = Math.round(numericValue);
  return roundedValue > 0 ? formatHaklaiWholeMoney(roundedValue) : "";
};

const hasDisplayableDepreciationTableAmount = (value) => Boolean(formatDepreciationTableAmount(value));

const getZeroResidentialDepreciationTooltip = (allocations = []) => {
  const relevantAllocations = Array.isArray(allocations) ? allocations : [];
  const hasValidZeroResult = relevantAllocations.length > 0 && relevantAllocations.every((allocation) => (
    allocation.isResidentialAutoDepreciationRelevant
    && !allocation.availableDepreciationValidationError
    && allocation.availableDepreciationResult === 0
  ));
  if (!hasValidZeroResult) return "";
  return relevantAllocations.every((allocation) => allocation.hasNoEligibleResidentialDepreciationPeriod)
    ? HAKLAI_EXPENSE_TOOLTIPS.noEligibleRentalPeriodAfterExpenseDate
    : HAKLAI_EXPENSE_TOOLTIPS.zeroCalculatedDepreciation;
};

const scrollFieldIntoView = (rootElement, fieldName) => {
  if (!rootElement || !fieldName) return false;
  const fieldElement = rootElement.querySelector(`[name="${fieldName}"], [data-field="${fieldName}"]`);
  if (!fieldElement) return false;
  const targetCell = fieldElement.closest("td");
  const scrollTarget = targetCell || fieldElement.closest(".MuiFormControl-root") || fieldElement;
  const horizontalScrollContainer = scrollTarget.closest(".MuiTableContainer-root");

  scrollTarget.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest"
  });

  if (
    horizontalScrollContainer
    && horizontalScrollContainer.scrollWidth > horizontalScrollContainer.clientWidth
  ) {
    const containerRect = horizontalScrollContainer.getBoundingClientRect();
    const targetRect = scrollTarget.getBoundingClientRect();
    const delta =
      (targetRect.left + targetRect.width / 2)
      - (containerRect.left + containerRect.width / 2);

    if (Math.abs(delta) > 1) {
      horizontalScrollContainer.scrollBy({
        left: delta,
        behavior: "smooth"
      });
    }
  }

  const focusTarget =
    scrollTarget.querySelector?.('[role="button"], input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])')
    || fieldElement;

  if (typeof focusTarget.focus === "function") {
    try {
      focusTarget.focus({ preventScroll: true });
    } catch (error) {
      focusTarget.focus();
    }
  }

  return true;
};

// `HaklaiExpenseRow` lives in its own file (`./HaklaiExpenseRow`) — same
// shape as the property-parts split (`HaklaiPropertyPartRows`). Keeping the
// section file focused on orchestration: data gathering, overlay build,
// sticky header, add-menu wiring, and the outer table chrome.

const HaklaiExpensesSection = React.memo(forwardRef((props, ref) => {
  const classes = useHaklaiExpensesStyles();
  const sectionRootRef = useRef(null);
  const isExpanded = props.expanded === true;
  const expenses = useMemo(() => props.expenses || [], [props.expenses]);
  const [hasPreparedExpenseContent, setHasPreparedExpenseContent] = useState(false);
  const [activeForm11DepreciationTarget, setActiveForm11DepreciationTarget] = useState(null);
  const handleAddExpense = props.handleAddExpense;

  // Every cross-table concern (committed/touched gating, row refs +
  // scroll/flash, lock-blur, sticky-header) now comes from the shared
  // controller hook. Property parts composes this exact hook too, so fixes
  // in either table land for both — see
  // `shared/useHaklaiTableValidationController.js`.
  const rowKeys = useMemo(() => expenses.map((expense) => expense.id), [expenses]);
  const {
    committedRows,
    touchedFieldsByRow,
    flashingRowKey,
    stickyHeaderEnabled,
    lockableContentRef,
    setStickyHeaderEnabled,
    setRowRef,
    touchRowField,
    commitAllRows,
    scrollToRow,
    flashRowWhenVisible
  } = useHaklaiTableValidationController({
    rowKeys,
    locked: props.locked,
    expanded: props.expanded
  });

  // Column-specific DOM ref maps stay local: they're only used by the
  // "focus-the-first-input-of-a-newly-added-row" path, which is an
  // expenses-only concern and doesn't belong in the shared hook.
  const expenseTypeInputRefs = useRef({});
  const amountInputRefs = useRef({});
  const currencyInputRefs = useRef({});
  const zabanInputRefs = useRef({});
  const expenseTypeOptions = useMemo(() => getHaklaiDeductionTypeOptions(), []);
  const zabanCheckboxOptions = useMemo(
    () => ZABAN_ATTRIBUTION_OPTIONS.filter((option) => option.value !== "both"),
    []
  );
  // The column stays even when this deal has no זב"ן. A row that cannot
  // use it shows a dash, so the table width does not jump.
  const showZabanColumn = true;
  const partsData = useMemo(() => getHaklaiLogicalPropertyPartsData(
    props.propertyPartsData || {},
    props.preCalc?.totalPropertyCost,
    props.hasZaban === true
  ), [props.hasZaban, props.preCalc?.totalPropertyCost, props.propertyPartsData]);
  const acquisitionAmount = props.preCalc?.acquisitionDetails?.amount;
  const tableFrameRef = useRef(null);
  const currencyOptionsByDateRef = useRef({});
  useEffect(() => {
    if (!isExpanded || hasPreparedExpenseContent) return undefined;
    return deferUntilAfterPaint(() => setHasPreparedExpenseContent(true));
  }, [hasPreparedExpenseContent, isExpanded]);

  const assetGroups = useMemo(
    () => (hasPreparedExpenseContent ? getHaklaiAssetGroups(partsData) : EMPTY_EXPENSE_LIST),
    [hasPreparedExpenseContent, partsData]
  );
  const assetGroupTitleByPartKey = useMemo(
    () => assetGroups.reduce((acc, assetGroup) => {
      acc[assetGroup.partKey] = assetGroup.title;
      return acc;
    }, {}),
    [assetGroups]
  );
  const expensesByPartKey = useMemo(
    () => {
      if (!hasPreparedExpenseContent) return EMPTY_EXPENSE_MAP;
      return expenses.reduce((acc, expense) => {
        const targetPartKey = expense?.targetPartKey || "";
        if (!targetPartKey || !partsData[targetPartKey]) return acc;
        if (!acc[targetPartKey]) acc[targetPartKey] = [];
        acc[targetPartKey].push(expense);
        return acc;
      }, {});
    },
    [expenses, hasPreparedExpenseContent, partsData]
  );
  const purchaseDateValue = useMemo(
    () => getHaklaiAcquisitionDisplayDate(props.preCalc || {}),
    [props.preCalc]
  );
  const purchaseDepreciationStartDate = props.purchaseDepreciationStartDate || purchaseDateValue;
  const seededExpenseTypeLabel = useMemo(
    () => getHaklaiSeededExpenseTypeLabel(props.preCalc || {}),
    [props.preCalc]
  );
  const purchaseCurrencyValue = useMemo(() => {
    return normalizeHaklaiCurrencyForDate(
      purchaseDateValue,
      props.preCalc?.acquisitionDetails?.deduCurrency || "newShekel"
    );
  }, [purchaseDateValue, props.preCalc?.acquisitionDetails?.deduCurrency]);
  const purchaseCurrencyOptions = useMemo(
    () => ensureHaklaiSelectedCurrencyOption(
      getHaklaiCurrencyOptions(purchaseDateValue || ""),
      purchaseCurrencyValue
    ),
    [purchaseCurrencyValue, purchaseDateValue]
  );
  const computedAcquisitionByPart = useMemo(
    () => (
      hasPreparedExpenseContent
        ? getHaklaiComputedAcquisitionByPart(partsData, acquisitionAmount)
        : EMPTY_EXPENSE_MAP
    ),
    [acquisitionAmount, hasPreparedExpenseContent, partsData]
  );
  const purchaseDepreciationByPart = useMemo(
    () => props.purchaseDepreciationByPart || {},
    [props.purchaseDepreciationByPart]
  );
  const purchaseDepreciationSelectionByPart = props.purchaseDepreciationSelectionByPart || {};
  const residentialRentalByPart = useMemo(
    () => props.residentialRentalByPart || {},
    [props.residentialRentalByPart]
  );
  const expenseTypeValueSet = useMemo(
    () => new Set(expenseTypeOptions.map((option) => option.value)),
    [expenseTypeOptions]
  );
  const expenseValidationContext = useMemo(
    () => (
      hasPreparedExpenseContent
        ? buildHaklaiExpenseValidationContext(partsData, props.preCalc?.totalPropertyCost, props.hasZaban === true)
        : null
    ),
    [hasPreparedExpenseContent, partsData, props.hasZaban, props.preCalc?.totalPropertyCost]
  );
  const validationStateById = useMemo(
    () => {
      if (!hasPreparedExpenseContent) return EMPTY_EXPENSE_MAP;
      return expenses.reduce((acc, expense) => {
        acc[expense.id] = getHaklaiExpenseValidationState({
          expense,
          soldPropertyDate: props.soldPropertyDate,
          ownershipStartDate: props.ownershipStartDate,
          propertyPartsData: partsData,
          hasZaban: props.hasZaban === true,
          validationContext: expenseValidationContext
        });
        return acc;
      }, {});
    },
    [
      expenseValidationContext,
      expenses,
      hasPreparedExpenseContent,
      partsData,
      props.hasZaban,
      props.ownershipStartDate,
      props.soldPropertyDate
    ]
  );
  const depreciationSummaryById = useMemo(
    () => {
      if (!hasPreparedExpenseContent) return EMPTY_EXPENSE_MAP;
      return expenses.reduce((acc, expense) => {
        acc[expense.id] = getHaklaiExpenseDepreciationSummary({
          expense,
          propertyPartsData: partsData,
          residentialRentalByPart,
          purchaseDepreciationByPart,
          soldPropertyDate: props.soldPropertyDate || "",
          ownershipStartDate: props.ownershipStartDate || ""
        });
        return acc;
      }, {});
    },
    [
      expenses,
      hasPreparedExpenseContent,
      partsData,
      purchaseDepreciationByPart,
      residentialRentalByPart,
      props.ownershipStartDate,
      props.soldPropertyDate
    ]
  );
  const expenseDepreciationSelectionCountByPart = useMemo(
    () => Object.values(depreciationSummaryById).reduce((acc, summary) => {
      (summary.allocations || []).forEach((allocation) => {
        if (!allocation?.partKey || !allocation.hasDepreciation) return;
        acc[allocation.partKey] = (acc[allocation.partKey] || 0) + 1;
      });
      return acc;
    }, {}),
    [depreciationSummaryById]
  );
  const showDepreciationSelectionLimitPopup = useCallback(() => {
    Swal.fire({
      title: HAKLAI_DEPRECIATION_SELECTION_LIMIT_TITLE,
      text: HAKLAI_DEPRECIATION_SELECTION_LIMIT_TEXT,
      icon: "warning",
      confirmButtonText: HAKLAI_EXPENSE_ACTIONS.confirmUnderstood
    });
  }, []);

  const getPurchaseDepreciationUiState = useCallback((partKey, part = {}) => {
    return getHaklaiPropertyDepreciationUiState({
      partKey,
      part,
      residentialRentalByPart,
      purchaseDepreciationByPart,
      soldPropertyDate: props.soldPropertyDate || "",
      purchaseDepreciationStartDate,
      acquisitionAmount: computedAcquisitionByPart[partKey]
    });
  }, [
    computedAcquisitionByPart,
    props.soldPropertyDate,
    purchaseDepreciationByPart,
    purchaseDepreciationStartDate,
    residentialRentalByPart
  ]);
  const purchaseDepreciationUiStateByPart = useMemo(
    () => assetGroups.reduce((acc, assetGroup) => {
      acc[assetGroup.partKey] = getPurchaseDepreciationUiState(
        assetGroup.partKey,
        partsData[assetGroup.partKey] || {}
      );
      return acc;
    }, {}),
    [assetGroups, getPurchaseDepreciationUiState, partsData]
  );
  const totalDepreciationSelectionCountByPart = useMemo(
    () => Object.entries(purchaseDepreciationUiStateByPart).reduce((acc, [partKey, depreciationState]) => {
      const isPurchaseSelected = purchaseDepreciationSelectionByPart[partKey] === true
        && depreciationState?.isComplete === true
        && hasDisplayableDepreciationTableAmount(depreciationState.depreciationResult);
      if (isPurchaseSelected) {
        acc[partKey] = (acc[partKey] || 0) + 1;
      }
      return acc;
    }, { ...expenseDepreciationSelectionCountByPart }),
    [
      expenseDepreciationSelectionCountByPart,
      purchaseDepreciationSelectionByPart,
      purchaseDepreciationUiStateByPart
    ]
  );
  const getFirstInvalidManualPurchaseDepreciationPartKey = useCallback(() => {
    const currentAssetGroups = getHaklaiAssetGroups(partsData);
    for (const assetGroup of currentAssetGroups) {
      const state = getPurchaseDepreciationUiState(
        assetGroup.partKey,
        partsData[assetGroup.partKey] || {}
      );
      if (
        state?.isManualAllowed === true
        && state?.hasManualConfigured === true
        && state?.manualValidationError
      ) {
        return assetGroup.partKey;
      }
    }
    return "";
  }, [getPurchaseDepreciationUiState, partsData]);
  // פחת מחושב and האם לנכות פחת stay in the table even when a row cannot
  // take depreciation. Those cells render a dash, so the table width stays fixed.
  const showDepreciationColumns = true;
  const currencyColumnWidth = useMemo(() => getHaklaiCurrencyColumnWidth([
    purchaseCurrencyValue,
    ...expenses.map((expense) => expense?.expenseCurrency)
  ]), [expenses, purchaseCurrencyValue]);
  const naturalTableWidth = getHaklaiExpensesTableWidth({
    showZabanColumn,
    showDepreciationColumns,
    currencyColumnWidth
  });
  const tableWidth = naturalTableWidth;
  const columnWidths = {
    type: EXPENSE_TYPE_COLUMN_WIDTH,
    date: DATE_COLUMN_WIDTH,
    amount: AMOUNT_COLUMN_WIDTH,
    currency: currencyColumnWidth,
    ...(showZabanColumn ? { zaban: ZABAN_COLUMN_WIDTH } : {}),
    ...(showDepreciationColumns ? {
      depreciationResult: DEPRECIATION_RESULT_COLUMN_WIDTH,
      depreciation: DEPRECIATION_COLUMN_WIDTH
    } : {})
  };
  const columnStyle = (width) => ({ width, minWidth: width, maxWidth: width });
  const isTableFit = useHaklaiTableFit(tableFrameRef, tableWidth);
  // Single source of truth for every error-reveal rule. The overlay merges
  // touched + committed state with the pure validation per row to produce
  // `cellErrors`, rendered by each cell's `FieldShell`.
  const validationOverlay = useMemo(() => {
    if (!hasPreparedExpenseContent) return EMPTY_VALIDATION_OVERLAY;
    return buildHaklaiExpensesValidationOverlay({
      expenses,
      assetGroups,
      expensesByPartKey,
      touchedFieldsByRow,
      committedRows,
      soldPropertyDate: props.soldPropertyDate,
      ownershipStartDate: props.ownershipStartDate,
      propertyPartsData: partsData,
      hasZaban: props.hasZaban === true,
      validationStateById,
      validationContext: expenseValidationContext
    });
  }, [
    assetGroups,
    committedRows,
    expenseValidationContext,
    expenses,
    expensesByPartKey,
    hasPreparedExpenseContent,
    partsData,
    props.hasZaban,
    props.ownershipStartDate,
    props.soldPropertyDate,
    touchedFieldsByRow,
    validationStateById
  ]);

  const commitAllRowsAndPrepareContent = useCallback(() => {
    setHasPreparedExpenseContent(true);
    commitAllRows();
  }, [commitAllRows]);

  const revealExpenseField = useCallback((expenseId, fieldName = "") => {
    if (!expenseId) return false;
    setHasPreparedExpenseContent(true);
    if (fieldName) touchRowField(expenseId, fieldName);
    afterNextPaint(() => {
      if (fieldName) {
        const didScroll = scrollFieldIntoView(
          sectionRootRef.current,
          getExpenseFieldDomName(expenseId, fieldName)
        );
        if (!didScroll) scrollToRow(expenseId);
      } else {
        scrollToRow(expenseId);
      }
      flashRowWhenVisible(expenseId);
    });
    return true;
  }, [flashRowWhenVisible, scrollToRow, touchRowField]);

  const scrollToSection = useCallback(() => {
    afterNextPaint(() => {
      sectionRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    });
    return true;
  }, []);

  const scrollToFirstPurchaseDepreciationIssue = useCallback(() => {
    setHasPreparedExpenseContent(true);
    const partKey = getFirstInvalidManualPurchaseDepreciationPartKey();
    if (!partKey) return false;
    afterNextPaint(() => {
      const didScroll = scrollFieldIntoView(
        sectionRootRef.current,
        `purchaseDepreciationResult_${partKey}`
      );
      if (!didScroll) scrollToSection();
    });
    return true;
  }, [getFirstInvalidManualPurchaseDepreciationPartKey, scrollToSection]);

  const getFirstInvalidExpenseTarget = useCallback(() => {
    const currentAssetGroups = getHaklaiAssetGroups(partsData);
    const currentExpensesByPartKey = expenses.reduce((acc, expense) => {
      const targetPartKey = expense?.targetPartKey || "";
      if (!targetPartKey || !partsData[targetPartKey]) return acc;
      if (!acc[targetPartKey]) acc[targetPartKey] = [];
      acc[targetPartKey].push(expense);
      return acc;
    }, {});
    const currentValidationContext = buildHaklaiExpenseValidationContext(
      partsData,
      props.preCalc?.totalPropertyCost,
      props.hasZaban === true
    );
    const buckets = [
      ...currentAssetGroups.map((assetGroup) => ({
        rows: currentExpensesByPartKey[assetGroup.partKey] || []
      }))
    ];

    for (const bucket of buckets) {
      for (const expense of bucket.rows) {
        const validationState = getHaklaiExpenseValidationState({
          expense,
          soldPropertyDate: props.soldPropertyDate,
          ownershipStartDate: props.ownershipStartDate,
          propertyPartsData: partsData,
          hasZaban: props.hasZaban === true,
          validationContext: currentValidationContext
        });
        if (validationState.isReusableDraft) continue;
        if (validationState.firstInvalidField) {
          return {
            expenseId: expense.id,
            fieldName: validationState.firstInvalidField
          };
        }
        const depreciationSummary = getHaklaiExpenseDepreciationSummary({
          expense,
          propertyPartsData: partsData,
          residentialRentalByPart,
          purchaseDepreciationByPart,
          soldPropertyDate: props.soldPropertyDate || "",
          ownershipStartDate: props.ownershipStartDate || ""
        });
        if (depreciationSummary.hasInvalidDepreciation) {
          return {
            expenseId: expense.id,
            fieldName: "depreciation"
          };
        }
      }
    }

    return null;
  }, [
    expenses,
    partsData,
    purchaseDepreciationByPart,
    props.hasZaban,
    props.ownershipStartDate,
    props.preCalc?.totalPropertyCost,
    props.soldPropertyDate,
    residentialRentalByPart
  ]);

  const revealFirstInvalidField = useCallback(() => {
    const target = getFirstInvalidExpenseTarget();
    if (!target) return false;
    return revealExpenseField(target.expenseId, target.fieldName);
  }, [getFirstInvalidExpenseTarget, revealExpenseField]);

  useImperativeHandle(ref, () => ({
    commitAllRows: commitAllRowsAndPrepareContent,
    revealFirstInvalidField,
    scrollToFirstPurchaseDepreciationIssue,
    scrollToSection
  }), [
    commitAllRowsAndPrepareContent,
    revealFirstInvalidField,
    scrollToFirstPurchaseDepreciationIssue,
    scrollToSection
  ]);

  const handleExpenseFieldChange = props.handleExpenseOnChange;

  const handleAmountChange = useCallback((expenseId, rawValue) => {
    const parsed = parseHaklaiClassicMoneyInput(rawValue, { maxDigits: MAX_MONEY_DIGITS });
    if (parsed.skip) return;
    handleExpenseFieldChange(expenseId, "amount", parsed.value);
  }, [handleExpenseFieldChange]);

  const getCompactCurrencyLabel = useCallback((currencyValue) => (
    COMPACT_CURRENCY_LABELS[currencyValue] || ""
  ), []);

  const getCurrencyOptionsForDate = useCallback((expenseDate = "") => {
    const cacheKey = expenseDate || "";
    if (!currencyOptionsByDateRef.current[cacheKey]) {
      currencyOptionsByDateRef.current[cacheKey] = getHaklaiCurrencyOptions(cacheKey);
    }
    return currencyOptionsByDateRef.current[cacheKey];
  }, []);

  const setFieldRef = useCallback((refStore, expenseId) => (input) => {
    if (input) {
      refStore.current[expenseId] = input;
      return;
    }
    delete refStore.current[expenseId];
  }, []);

  const renderReadOnlyField = useCallback((
    value,
    className,
    adornment = null,
    fieldName = "",
    shellClassName = "",
    startAdornment = null
  ) => (
    <FieldShell classes={classes} shellClassName={shellClassName}>
      <TextField
        name={fieldName || undefined}
        variant="outlined"
        size="small"
        value={value}
        disabled
        className={[classes.readOnlyField, className].filter(Boolean).join(" ")}
        InputProps={
          adornment || startAdornment
            ? {
              ...(startAdornment ? {
                startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>
              } : {}),
              ...(adornment ? {
                endAdornment: <InputAdornment position="end">{adornment}</InputAdornment>
              } : {})
            }
            : undefined
        }
      />
    </FieldShell>
  ), [classes]);

  const renderDisabledAmountCurrencyCells = useCallback((amountValue, currencyValue, fieldNamePrefix = "") => (
    <React.Fragment>
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.amountFieldWrap}`}>
          <FieldShell classes={classes} shellClassName={classes.amountFieldShell}>
            <TextField
              name={fieldNamePrefix ? `${fieldNamePrefix}Amount` : undefined}
              type="text"
              variant="outlined"
              size="small"
              value={Number.isFinite(amountValue) && amountValue > 0 ? formatHaklaiWholeMoney(amountValue) : ""}
              disabled
              className={[classes.amountInput, classes.disabledInlineField, "haklai-money-input"].join(" ")}
              InputProps={{ readOnly: true }}
              inputProps={{ readOnly: true, tabIndex: -1 }}
            />
          </FieldShell>
        </Box>
      </StyledCell>
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.currencyFieldWrap}`}>
          <FieldShell classes={classes} shellClassName={classes.currencyFieldShell}>
            <TextField
              name={fieldNamePrefix ? `${fieldNamePrefix}Currency` : undefined}
              select
              variant="outlined"
              size="small"
              value={currencyValue || "newShekel"}
              disabled
              className={[
                classes.currencySelectInput,
                classes.disabledInlineField,
                classes.disabledCurrencySelectInput
              ].join(" ")}
            >
              {purchaseCurrencyOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {COMPACT_CURRENCY_LABELS[option.value] || option.label}
                </MenuItem>
              ))}
            </TextField>
          </FieldShell>
        </Box>
      </StyledCell>
    </React.Fragment>
  ), [classes, purchaseCurrencyOptions]);

  const renderNonRelevantCell = useCallback((fieldName = "", label = "—") => (
    <StyledCell
      align="center"
      className={classes.nonRelevantCell}
      data-field={fieldName || undefined}
    >
      <Typography className={classes.nonRelevantText}>
        {label}
      </Typography>
    </StyledCell>
  ), [classes]);

  const renderQuietValuePlaceholder = useCallback((fieldName = "", label = "—", tooltipTitle = "") => (
    <StyledCell align="center" data-field={fieldName || undefined}>
      <Box className={`${classes.columnFieldWrap} ${classes.depreciationFieldWrap}`}>
        <Box className={classes.depreciationFieldContent}>
          <Tooltip title={tooltipTitle} disableHoverListener={!tooltipTitle}>
            <Typography
              component="span"
              className={[
                classes.mutedCellText,
                tooltipTitle ? classes.explainedMutedCellText : ""
              ].filter(Boolean).join(" ")}
              aria-label={tooltipTitle || undefined}
            >
              {label}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
    </StyledCell>
  ), [classes]);

  const renderDepreciationResultCell = useCallback(({
    amount,
    currency,
    fieldName = "",
    errorMsg = "",
    displayZero = false,
    tooltipTitle = ""
  } = {}) => {
    const numericAmount = Number(amount);
    const formattedAmount = displayZero && numericAmount === 0
      ? "0"
      : formatDepreciationTableAmount(amount);
    const hasError = Boolean(errorMsg);
    const hasAmount = !hasError && Boolean(formattedAmount);
    const currencyLabel = getCompactCurrencyLabel(currency);
    const errorId = buildFieldErrorId(fieldName, "depreciationResult");
    return (
      <StyledCell align="center" data-field={fieldName || undefined}>
        <Box className={`${classes.columnFieldWrap} ${classes.depreciationResultFieldWrap}`}>
          <FieldShell
            classes={classes}
            shellClassName={hasError
              ? classes.depreciationResultFieldShellWithError
              : classes.depreciationResultFieldShell}
            errorMsg={errorMsg}
            errorId={errorId}
          >
            <Tooltip title={tooltipTitle} disableHoverListener={!tooltipTitle}>
              <Box
                className={[
                  classes.depreciationResultDisplay,
                  hasError ? classes.depreciationResultDisplayError : "",
                  tooltipTitle ? classes.explainedMutedCellText : ""
                ].filter(Boolean).join(" ")}
                aria-label={tooltipTitle || undefined}
                aria-invalid={hasError || undefined}
                aria-describedby={hasError ? errorId : undefined}
              >
                <span className={classes.depreciationResultDisplayCurrency} aria-hidden="true">
                  {hasAmount ? (currencyLabel || "") : ""}
                </span>
                <span
                  className={classes.depreciationResultDisplayValue}
                  aria-label={HAKLAI_EXPENSE_LABELS.calculatedDepreciation}
                >
                  {hasAmount ? formattedAmount : ""}
                </span>
              </Box>
            </Tooltip>
          </FieldShell>
        </Box>
      </StyledCell>
    );
  }, [classes, getCompactCurrencyLabel]);

  const renderForm11DepreciationButtonCell = useCallback(({
    fieldName = ""
  }, {
    hasConfigured = false,
    hasInvalid = false,
    amount = null,
    currency = "newShekel",
    tooltipTitle = "",
    onClick
  } = {}) => {
    const formattedAmount = hasConfigured ? formatDepreciationTableAmount(amount) : "";
    const currencyLabel = getCompactCurrencyLabel(currency);
    if (formattedAmount && !hasInvalid) {
      return (
        <StyledCell align="center" data-field={fieldName || undefined}>
          <Box className={`${classes.columnFieldWrap} ${classes.depreciationResultFieldWrap}`}>
            <FieldShell
              classes={classes}
              shellClassName={classes.depreciationResultFieldShell}
            >
              <Tooltip title={tooltipTitle || HAKLAI_EXPENSE_TOOLTIPS.editForm11DepreciationAmount}>
                <ButtonBase
                  name={fieldName || undefined}
                  data-field={fieldName || undefined}
                  className={classes.depreciationAmountEditButton}
                  onClick={onClick}
                >
                  <span className={classes.depreciationAmountEditButtonCurrency}>
                    {currencyLabel}
                  </span>
                  <span className={classes.depreciationAmountEditButtonValue}>
                    {formattedAmount}
                  </span>
                  <CalculatorIcon className={classes.depreciationAmountEditButtonIcon} />
                </ButtonBase>
              </Tooltip>
            </FieldShell>
          </Box>
        </StyledCell>
      );
    }

    return (
      <StyledCell align="center" data-field={fieldName || undefined}>
        <Box className={`${classes.columnFieldWrap} ${classes.depreciationResultFieldWrap}`}>
          <Box className={classes.depreciationFieldContent}>
            <Tooltip title={tooltipTitle || HAKLAI_EXPENSE_TOOLTIPS.enterForm11DepreciationAmount}>
              <span>
                <ButtonBase
                  name={fieldName || undefined}
                  data-field={fieldName || undefined}
                  className={[
                    classes.depreciationEditButton,
                    classes.depreciationEditButtonResultSize,
                    hasConfigured ? "" : classes.depreciationEditButtonEmpty,
                    hasInvalid ? classes.depreciationEditButtonWarning : "",
                    hasConfigured && !hasInvalid ? classes.depreciationEditButtonComplete : ""
                  ].filter(Boolean).join(" ")}
                  onClick={onClick}
                >
                  {renderDepreciationButtonCopy(
                    classes,
                    getForm11ButtonCopy({ hasConfigured, hasInvalid })
                  )}
                </ButtonBase>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </StyledCell>
    );
  }, [classes, getCompactCurrencyLabel]);

  const handleSetExpenseDepreciationForAllocations = useCallback((
    expense,
    allocations = [],
    enabled = true
  ) => {
    if (!expense?.id) return;
    const allocationList = (Array.isArray(allocations) ? allocations : [allocations])
      .filter((allocation) => (
        allocation?.partKey
        && (
          allocation.isResidentialAutoDepreciationRelevant
          || allocation.isSelectionOnlyDepreciationRelevant
        )
      ));
    if (!allocationList.length) return;
    const nextDepreciationByPart = {
      ...(expense.depreciationByPart || {})
    };
    allocationList.forEach((allocation) => {
      if (enabled) {
        const existingDepreciation = normalizeHaklaiDepreciationState(
          nextDepreciationByPart[allocation.partKey]
        );
        nextDepreciationByPart[allocation.partKey] = allocation.isResidentialAutoDepreciationRelevant
          ? { decision: HAKLAI_EXPENSE_DEPRECIATION_DECISION.RESIDENTIAL_AUTO }
          : {
            ...existingDepreciation,
            decision: HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11,
            depCurrency: existingDepreciation.depCurrency || "newShekel"
          };
      } else {
        const existingDepreciation = normalizeHaklaiDepreciationState(
          nextDepreciationByPart[allocation.partKey]
        );
        if (
          allocation.isSelectionOnlyDepreciationRelevant
          && hasPositiveDepreciationResult(existingDepreciation)
        ) {
          nextDepreciationByPart[allocation.partKey] = {
            ...existingDepreciation,
            decision: HAKLAI_EXPENSE_DEPRECIATION_DECISION.NONE,
            depCurrency: existingDepreciation.depCurrency || "newShekel"
          };
        } else {
          delete nextDepreciationByPart[allocation.partKey];
        }
      }
    });
    handleExpenseFieldChange(
      expense.id,
      "depreciationByPart",
      nextDepreciationByPart
    );
  }, [handleExpenseFieldChange]);

  const hasConfiguredDepreciationProfileForAllocation = useCallback((allocation = {}) => {
    if (
      !allocation.partKey
      || (
        !allocation.isResidentialAutoDepreciationRelevant
        && !allocation.isSelectionOnlyDepreciationRelevant
      )
    ) {
      return false;
    }
    if (allocation.isSelectionOnlyDepreciationRelevant) return true;
    return purchaseDepreciationUiStateByPart[allocation.partKey]?.hasConfigured === true;
  }, [purchaseDepreciationUiStateByPart]);

  const hasRequiredDepreciationProfileForAllocation = useCallback((allocation = {}) => {
    if (allocation.isSelectionOnlyDepreciationRelevant) return true;
    if (!hasConfiguredDepreciationProfileForAllocation(allocation)) return false;
    return purchaseDepreciationUiStateByPart[allocation.partKey]?.isComplete === true;
  }, [hasConfiguredDepreciationProfileForAllocation, purchaseDepreciationUiStateByPart]);

  const hasVisibleDepreciationSelectionForAllocation = useCallback((allocation = {}) => {
    // Form 11 parts such as יתרת המשק have no calculated amount until the
    // user types one. The checkbox is still the selection, same as a
    // residence row, and the four-record cap applies when it is checked.
    if (allocation.isSelectionOnlyDepreciationRelevant) return true;
    return hasConfiguredDepreciationProfileForAllocation(allocation)
      && hasDisplayableDepreciationTableAmount(allocation.availableDepreciationResult);
  }, [hasConfiguredDepreciationProfileForAllocation]);

  const isDepreciationSelectionLimitReachedForPart = useCallback((partKey = "") => {
    if (!partKey) return false;
    const selectedCount = totalDepreciationSelectionCountByPart[partKey] || 0;
    return selectedCount >= HAKLAI_MAX_EXPENSE_DEPRECIATION_SELECTIONS_PER_PART;
  }, [totalDepreciationSelectionCountByPart]);

  const isExpenseDepreciationSelectionLimitReached = useCallback((allocation = {}) => {
    if (!allocation.partKey || allocation.hasDepreciation) return false;
    return isDepreciationSelectionLimitReachedForPart(allocation.partKey);
  }, [isDepreciationSelectionLimitReachedForPart]);

  const hasDepreciationSelectionLimitIssue = useCallback((allocations = []) => {
    const allocationList = (Array.isArray(allocations) ? allocations : [allocations])
      .filter((allocation) => allocation?.partKey);
    return allocationList.some(isExpenseDepreciationSelectionLimitReached);
  }, [isExpenseDepreciationSelectionLimitReached]);

  const closeForm11DepreciationDialog = useCallback(() => {
    setActiveForm11DepreciationTarget(null);
  }, []);

  const openPurchaseForm11DepreciationDialog = useCallback((partKey) => {
    if (!partKey) return;
    setActiveForm11DepreciationTarget({
      type: "purchase",
      partKey
    });
  }, []);

  const openExpenseForm11DepreciationDialog = useCallback((expense, allocation = {}) => {
    if (!expense?.id || !allocation.partKey) return;
    if (!allocation.hasDepreciation && isDepreciationSelectionLimitReachedForPart(allocation.partKey)) {
      showDepreciationSelectionLimitPopup();
      return;
    }
    setActiveForm11DepreciationTarget({
      type: "expense",
      expenseId: expense.id,
      partKey: allocation.partKey
    });
  }, [
    isDepreciationSelectionLimitReachedForPart,
    showDepreciationSelectionLimitPopup
  ]);

  const saveExpenseForm11Depreciation = useCallback((expense, partKey, nextDepreciation = {}) => {
    if (!expense?.id || !partKey) return;
    const normalizedDepreciation = normalizeHaklaiDepreciationState(nextDepreciation);
    const existingDecision = getHaklaiExpenseDepreciationDecision(
      expense.depreciationByPart?.[partKey]
    );
    const nextDecision = existingDecision === HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11
      ? HAKLAI_EXPENSE_DEPRECIATION_DECISION.FORM_11
      : HAKLAI_EXPENSE_DEPRECIATION_DECISION.NONE;
    const nextDepreciationByPart = {
      ...(expense.depreciationByPart || {})
    };
    if (Object.keys(normalizedDepreciation).length > 0) {
      nextDepreciationByPart[partKey] = {
        ...normalizedDepreciation,
        decision: nextDecision,
        depCurrency: normalizedDepreciation.depCurrency || "newShekel"
      };
    } else {
      delete nextDepreciationByPart[partKey];
    }
    handleExpenseFieldChange(
      expense.id,
      "depreciationByPart",
      nextDepreciationByPart
    );
  }, [handleExpenseFieldChange]);

  const renderResidentialExpenseDepreciationCheckbox = useCallback(({
    fieldName = "",
    checked = false,
    indeterminate = false,
    disabled = false,
    locked = false,
    hasInvalid = false,
    tooltipTitle = "",
    onChange
  }) => (
    <Tooltip title={tooltipTitle}>
      <Box
        component="label"
        data-field={fieldName || undefined}
        aria-disabled={disabled ? "true" : undefined}
        aria-label={tooltipTitle || undefined}
        className={[
          classes.depreciationCheckboxContent,
          locked ? classes.depreciationCheckboxContentLocked : "",
          disabled && !locked ? classes.depreciationCheckboxContentDisabled : ""
        ].filter(Boolean).join(" ")}
      >
        <Checkbox
          size="small"
          color="primary"
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          onChange={disabled ? undefined : onChange}
          className={[
            classes.depreciationCheckbox,
            locked ? classes.depreciationCheckboxLocked : "",
            hasInvalid ? classes.depreciationCheckboxWarning : ""
          ].filter(Boolean).join(" ")}
          inputProps={{
            "aria-label": HAKLAI_EXPENSE_LABELS.depreciationDeductionAria
          }}
        />
      </Box>
    </Tooltip>
  ), [classes]);

  const renderUnavailableDepreciationCell = useCallback((fieldName = "", tooltipTitle = "") => (
    renderQuietValuePlaceholder(fieldName, "—", tooltipTitle)
  ), [renderQuietValuePlaceholder]);

  const renderExpenseDepreciationField = useCallback((expense, fieldName = "") => {
    if (!String(expense?.expenseType || "").trim() || !expense?.targetPartKey) {
      return renderUnavailableDepreciationCell(fieldName);
    }
    const summary = depreciationSummaryById[expense.id] || {};
    const relevantAllocations = (summary.allocations || [])
      .filter((allocation) => allocation.isRelevant);
    if (!relevantAllocations.length) return renderUnavailableDepreciationCell(fieldName);
    const zeroDepreciationTooltip = getZeroResidentialDepreciationTooltip(relevantAllocations);
    const visibleAllocations = relevantAllocations.filter(hasVisibleDepreciationSelectionForAllocation);
    if (!visibleAllocations.length) {
      return renderUnavailableDepreciationCell(fieldName, zeroDepreciationTooltip || "");
    }

    const isMultiAllocation = visibleAllocations.length > 1;
    const hasInvalid = visibleAllocations.some((allocation) => allocation.hasDepreciation && allocation.validationError);
    const configuredCount = visibleAllocations.filter((allocation) => allocation.hasDepreciation).length;
    const singleAllocation = !isMultiAllocation ? visibleAllocations[0] : null;

    const profileAllocations = visibleAllocations.filter(hasConfiguredDepreciationProfileForAllocation);
    const selectableAllocations = profileAllocations.filter(hasRequiredDepreciationProfileForAllocation);
    const configuredSelectableCount = selectableAllocations.filter((allocation) => allocation.hasDepreciation).length;
    const hasSelectableAllocation = selectableAllocations.length > 0;
    const hasConfiguredWithoutProfile = configuredCount > configuredSelectableCount;
    const hasLimitReachedAllocation = selectableAllocations.some(isExpenseDepreciationSelectionLimitReached);
    const hasIncompleteProfileAllocation = profileAllocations.some((allocation) => (
      !hasRequiredDepreciationProfileForAllocation(allocation)
    ));
    const invalidStateHidesCheckbox = hasInvalid && visibleAllocations.some((allocation) => (
      allocation.validationError
      && allocation.validationError !== HAKLAI_EXPENSE_ERRORS.form11DepreciationAmountRequired
    ));
    if (
      !hasSelectableAllocation
      || invalidStateHidesCheckbox
      || hasConfiguredWithoutProfile
      || hasIncompleteProfileAllocation
    ) {
      return renderUnavailableDepreciationCell(fieldName);
    }

    const checkboxChecked = isMultiAllocation
      ? configuredSelectableCount === selectableAllocations.length
      : Boolean(singleAllocation?.hasDepreciation);
    const checkboxIndeterminate = isMultiAllocation && (
      configuredSelectableCount > 0 && configuredSelectableCount < selectableAllocations.length
    );
    const form11AmountError = singleAllocation?.isSelectionOnlyDepreciationRelevant
      && singleAllocation?.validationError === HAKLAI_EXPENSE_ERRORS.form11DepreciationAmountRequired
      ? singleAllocation.validationError
      : "";
    const tooltipTitle = hasLimitReachedAllocation && !checkboxChecked
      ? HAKLAI_EXPENSE_ERRORS.depreciationSelectionLimit
      : form11AmountError
        ? form11AmountError
        : singleAllocation?.isSelectionOnlyDepreciationRelevant
        ? checkboxChecked
          ? HAKLAI_EXPENSE_TOOLTIPS.removeForm11Depreciation
          : HAKLAI_EXPENSE_TOOLTIPS.includeForm11Depreciation
        : checkboxIndeterminate && !checkboxChecked
          ? HAKLAI_EXPENSE_TOOLTIPS.includeDmmDepreciationForAll
          : checkboxChecked
            ? HAKLAI_EXPENSE_TOOLTIPS.removeDmmDepreciation
            : HAKLAI_EXPENSE_TOOLTIPS.includeDmmDepreciationForPart;
    const handleExpenseDepreciationCheckboxChange = (event) => {
      if (!event.target.checked) {
        handleSetExpenseDepreciationForAllocations(
          expense,
          isMultiAllocation ? selectableAllocations : [singleAllocation],
          false
        );
        return;
      }
      if (!hasSelectableAllocation) return;
      const targetAllocations = isMultiAllocation ? selectableAllocations : [singleAllocation];
      if (hasDepreciationSelectionLimitIssue(targetAllocations)) {
        showDepreciationSelectionLimitPopup();
        return;
      }
      handleSetExpenseDepreciationForAllocations(
        expense,
        targetAllocations,
        true
      );
    };

    return (
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.depreciationFieldWrap}`}>
          <Box className={classes.depreciationFieldContent}>
            {renderResidentialExpenseDepreciationCheckbox({
              fieldName,
              checked: checkboxChecked,
              indeterminate: checkboxIndeterminate,
              disabled: false,
              hasInvalid: false,
              tooltipTitle,
              onChange: handleExpenseDepreciationCheckboxChange
            })}
          </Box>
        </Box>
      </StyledCell>
    );
  }, [
    classes,
    depreciationSummaryById,
    handleSetExpenseDepreciationForAllocations,
    hasConfiguredDepreciationProfileForAllocation,
    hasDepreciationSelectionLimitIssue,
    hasRequiredDepreciationProfileForAllocation,
    hasVisibleDepreciationSelectionForAllocation,
    isExpenseDepreciationSelectionLimitReached,
    renderResidentialExpenseDepreciationCheckbox,
    renderUnavailableDepreciationCell,
    showDepreciationSelectionLimitPopup
  ]);

  const renderExpenseDepreciationResultCell = useCallback((expense, fieldName = "") => {
    const summary = depreciationSummaryById[expense.id] || {};
    const relevantAllocations = (summary.allocations || [])
      .filter((allocation) => allocation.isRelevant);
    const zeroDepreciationTooltip = getZeroResidentialDepreciationTooltip(relevantAllocations);
    const hasProfileAllocation = relevantAllocations.some(hasConfiguredDepreciationProfileForAllocation);
    if (!String(expense?.expenseType || "").trim() || !expense?.targetPartKey || !hasProfileAllocation) {
      return renderQuietValuePlaceholder(fieldName, "—");
    }
    const manualAllocation = relevantAllocations.find((allocation) => (
      allocation.isSelectionOnlyDepreciationRelevant
    ));
    if (manualAllocation) {
      const hasConfigured = hasPositiveDepreciationResult(manualAllocation.depreciation);
      const hasInvalid = manualAllocation.hasDepreciation && Boolean(manualAllocation.validationError);
      return renderForm11DepreciationButtonCell({ fieldName }, {
        hasConfigured,
        hasInvalid,
        amount: manualAllocation.depreciation.depreciationResult,
        currency: manualAllocation.depreciation.depCurrency || "newShekel",
        tooltipTitle: hasInvalid
          ? manualAllocation.validationError
          : hasConfigured
            ? getHaklaiExpenseForm11EditTooltip(
              formatHaklaiWholeMoney(Number(manualAllocation.depreciation.depreciationResult))
            )
            : HAKLAI_EXPENSE_TOOLTIPS.enterForm11DepreciationAmount,
        onClick: () => openExpenseForm11DepreciationDialog(expense, manualAllocation)
      });
    }
    if (!hasDisplayableDepreciationTableAmount(summary.totalAvailableDepreciationResult)) {
      if (zeroDepreciationTooltip) {
        return renderDepreciationResultCell({
          amount: 0,
          currency: summary.displayCurrency || expense.expenseCurrency,
          fieldName,
          displayZero: true,
          tooltipTitle: zeroDepreciationTooltip
        });
      }
      return renderQuietValuePlaceholder(fieldName, "—");
    }
    return renderDepreciationResultCell({
      amount: summary.totalAvailableDepreciationResult,
      currency: summary.displayCurrency || expense.expenseCurrency,
      fieldName
    });
  }, [
    depreciationSummaryById,
    hasConfiguredDepreciationProfileForAllocation,
    openExpenseForm11DepreciationDialog,
    renderDepreciationResultCell,
    renderForm11DepreciationButtonCell,
    renderQuietValuePlaceholder
  ]);

  const renderPurchaseDepreciationField = (partKey, depreciationState) => {
    const fieldName = `purchaseDepreciation_${partKey}`;
    // The amount has to exist before it can be chosen: a calculated rental
    // amount on a house, or a saved טופס י״א amount on a non-apartment part.
    // Checking it is the user's choice, and it counts toward the four records.
    const hasSelectableAmount = depreciationState?.isRelevant === true
      && depreciationState?.hasInvalid !== true
      && depreciationState?.isComplete === true
      && hasDisplayableDepreciationTableAmount(depreciationState.depreciationResult);
    if (!hasSelectableAmount) return renderUnavailableDepreciationCell(fieldName);
    const isSelected = purchaseDepreciationSelectionByPart[partKey] === true;
    const isLimitReached = !isSelected && isDepreciationSelectionLimitReachedForPart(partKey);
    const handlePurchaseCheckboxChange = (event) => {
      if (!event.target.checked) {
        props.handlePurchaseDepreciationSelectionChange?.(partKey, false);
        return;
      }
      if (isDepreciationSelectionLimitReachedForPart(partKey)) {
        showDepreciationSelectionLimitPopup();
        return;
      }
      props.handlePurchaseDepreciationSelectionChange?.(partKey, true);
    };
    return (
      <StyledCell align="center">
        <Box className={`${classes.columnFieldWrap} ${classes.depreciationFieldWrap}`}>
          <Box className={classes.depreciationFieldContent}>
            {renderResidentialExpenseDepreciationCheckbox({
              fieldName,
              checked: isSelected,
              disabled: false,
              tooltipTitle: isLimitReached
                ? HAKLAI_EXPENSE_ERRORS.depreciationSelectionLimit
                : isSelected
                  ? HAKLAI_EXPENSE_TOOLTIPS.removePurchaseDepreciation
                  : HAKLAI_EXPENSE_TOOLTIPS.includePurchaseDepreciation,
              onChange: handlePurchaseCheckboxChange
            })}
          </Box>
        </Box>
      </StyledCell>
    );
  };

  const renderPurchaseDepreciationResultField = (partKey, depreciationState) => {
    const fieldName = `purchaseDepreciationResult_${partKey}`;
    if (depreciationState.isManualAllowed === true) {
      const manualDepreciation = depreciationState.manualDepreciation || {};
      const hasConfigured = hasPositiveDepreciationResult(manualDepreciation);
      const hasInvalid = depreciationState.hasManualConfigured === true
        && Boolean(depreciationState.manualValidationError);
      return renderForm11DepreciationButtonCell({ fieldName }, {
        hasConfigured,
        hasInvalid,
        amount: manualDepreciation.depreciationResult,
        currency: manualDepreciation.depCurrency || "newShekel",
        tooltipTitle: hasInvalid
          ? depreciationState.manualValidationError
          : hasConfigured
            ? getHaklaiExpenseForm11EditTooltip(
              formatHaklaiWholeMoney(Number(manualDepreciation.depreciationResult))
            )
            : HAKLAI_EXPENSE_TOOLTIPS.enterPurchaseForm11DepreciationAmount,
        onClick: () => openPurchaseForm11DepreciationDialog(partKey)
      });
    }

    return (
      depreciationState.hasConfigured === true
      && depreciationState.hasInvalid !== true
      && hasDisplayableDepreciationTableAmount(depreciationState.depreciationResult)
    )
      ? renderDepreciationResultCell({
        amount: depreciationState.depreciationResult,
        currency: purchaseCurrencyValue,
        fieldName
      })
      : renderQuietValuePlaceholder(fieldName, "—");
  };

  const getAssetSectionLabel = (assetGroup) => {
    const headerTitle = getHaklaiExpenseDeductionsHeader(assetGroup.title);
    const partCategory = partsData[assetGroup.partKey]?.category;
    const showCommercialBadge = (
      partCategory === CATEGORIES.COMMERCIAL
      || partCategory === CATEGORIES.COMMERCIAL_BG
      || partCategory === CATEGORIES.LAND_AND_SHARED
    );
    const badge = showCommercialBadge ? (
      <Tooltip title={HAKLAI_EXPENSE_TOOLTIPS.commercialExpenseGroup}>
        <span className={classes.assetHeaderCommercialBadge}>
          {HAKLAI_EXPENSE_LABELS.commercialExpenseGroupBadge}
        </span>
      </Tooltip>
    ) : null;
    return { headerTitle, badge };
  };

  const renderPurchaseZabanCell = (partKey) => {
    const fieldName = `purchaseZabanAttribution_${partKey}`;
    if (!isHaklaiZabanAttributionRelevant(partsData, partKey, props.hasZaban === true)) {
      return renderNonRelevantCell(fieldName);
    }
    const selectedZabanValues = getHaklaiZabanAttributionSelections(DEFAULT_HAKLAI_ZABAN_ATTRIBUTION);
    return (
      <StyledCell align="center" data-field={fieldName}>
        <Box className={`${classes.zabanCheckboxCellContent} ${classes.zabanFieldWrap}`}>
          <FieldShell classes={classes} shellClassName={classes.zabanFieldShell}>
            <Box
              className={`${classes.zabanCheckboxField} ${classes.zabanCheckboxFieldMuted}`}
              aria-label={HAKLAI_EXPENSE_LABELS.purchaseZabanAttribution}
            >
              {zabanCheckboxOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  className={classes.zabanCheckboxOption}
                  labelPlacement="end"
                  control={(
                    <Checkbox
                      size="small"
                      color="primary"
                      name={fieldName}
                      id={`${fieldName}_${option.value}`}
                      checked={selectedZabanValues.includes(option.value)}
                      disabled
                    />
                  )}
                  label={option.label}
                />
              ))}
            </Box>
          </FieldShell>
        </Box>
      </StyledCell>
    );
  };

  const renderPurchaseRow = (partKey, { headerTitle = "", badge = null } = {}) => {
    const part = partsData[partKey];
    const depreciationState = purchaseDepreciationUiStateByPart[partKey]
      || getPurchaseDepreciationUiState(partKey, part);

    return (
      <TableRow key={`purchase_${partKey}`} className={classes.expenseDataRow}>
        <HaklaiSectionLabelCell
          title={headerTitle}
          badge={badge}
          addButton={{
            classes,
            onClick: () => handleAddExpense(partKey),
            readyLabel: HAKLAI_EXPENSE_ACTIONS.addExpenseRow,
            unavailableLabel: HAKLAI_EXPENSE_ACTIONS.unavailableAddExpenseRow
          }}
        />
        <StyledCell align="center">
          <Box className={`${classes.columnFieldWrap} ${classes.expenseTypeFieldWrap}`}>
            <Tooltip title={seededExpenseTypeLabel}>
              <span className={classes.expenseTypeTooltipAnchor}>
                {renderReadOnlyField(
                  seededExpenseTypeLabel,
                  [classes.typeReadOnlyField, classes.inertDisabledField].join(" "),
                  null,
                  `purchaseExpenseType_${partKey}`,
                  classes.expenseTypeFieldShell
                )}
              </span>
            </Tooltip>
          </Box>
        </StyledCell>
        <StyledCell align="center">
          <Box className={`${classes.columnFieldWrap} ${classes.dateFieldWrap}`}>
            <FieldShell classes={classes} shellClassName={classes.dateFieldShell}>
              <DatePickerField
                name={`purchaseDate_${partKey}`}
                className={[classes.dateInput, "haklai-date-input"].join(" ")}
                textFieldWidth={DATE_INPUT_WIDTH}
                exactWidth
                value={purchaseDateValue || ""}
                disabled
                suppressHelperText
                inputProps={{ readOnly: true, tabIndex: -1 }}
              />
            </FieldShell>
          </Box>
        </StyledCell>
        {renderDisabledAmountCurrencyCells(
          computedAcquisitionByPart[partKey],
          purchaseCurrencyValue,
          `purchase_${partKey}_`
        )}
        {showZabanColumn ? (
          renderPurchaseZabanCell(partKey)
        ) : null}
        {showDepreciationColumns ? renderPurchaseDepreciationResultField(partKey, depreciationState) : null}
        {showDepreciationColumns ? renderPurchaseDepreciationField(partKey, depreciationState) : null}
        <StyledCell align="center" className={[classes.actionCell, "haklai-icon-cell"].join(" ")}>
          <Box className={classes.rowActionCellContent} />
        </StyledCell>
      </TableRow>
    );
  };

  const renderExpenseRow = (expense, siblingExpenses) => {
    const validationState = validationStateById[expense.id] || EMPTY_VALIDATION_STATE;
    const isIgnorableTrailingRow =
      siblingExpenses[siblingExpenses.length - 1]?.id === expense.id && validationState.isReusableDraft;
    const currencyOptions = ensureHaklaiSelectedCurrencyOption(
      getCurrencyOptionsForDate(expense.expenseDate || ""),
      expense.expenseCurrency
    );
    const rowCellErrors = validationOverlay.cellErrors[expense.id] || EMPTY_ROW_FIELDS;

    return (
      <HaklaiExpenseRow
        key={expense.id}
        expense={expense}
        isIgnorableTrailingRow={isIgnorableTrailingRow}
        validationState={validationState}
        cellErrors={rowCellErrors}
        isFlashing={flashingRowKey === expense.id}
        classes={classes}
        showZabanColumn={showZabanColumn}
        showDepreciationColumns={showDepreciationColumns}
        expenseTypeValueSet={expenseTypeValueSet}
        expenseTypeOptions={expenseTypeOptions}
        currencyOptions={currencyOptions}
        zabanCheckboxOptions={zabanCheckboxOptions}
        handleExpenseFieldChange={handleExpenseFieldChange}
        touchRowField={touchRowField}
        handleAmountChange={handleAmountChange}
        handleRemoveExpense={props.handleRemoveExpense}
        setFieldRef={setFieldRef}
        setRowRef={setRowRef}
        expenseTypeInputRefs={expenseTypeInputRefs}
        amountInputRefs={amountInputRefs}
        currencyInputRefs={currencyInputRefs}
        zabanInputRefs={zabanInputRefs}
        renderExpenseDepreciationField={renderExpenseDepreciationField}
        renderExpenseDepreciationResultCell={renderExpenseDepreciationResultCell}
        renderNonRelevantCell={renderNonRelevantCell}
      />
    );
  };

  const activeForm11PartKey = activeForm11DepreciationTarget?.partKey || "";
  const activeForm11Part = activeForm11PartKey ? partsData[activeForm11PartKey] : null;
  const activeForm11Expense = activeForm11DepreciationTarget?.type === "expense"
    ? expenses.find((expense) => expense.id === activeForm11DepreciationTarget.expenseId)
    : null;
  const activeForm11Depreciation = activeForm11DepreciationTarget?.type === "purchase"
    ? purchaseDepreciationUiStateByPart[activeForm11PartKey]?.manualDepreciation || {}
    : normalizeHaklaiDepreciationState(
      activeForm11Expense?.depreciationByPart?.[activeForm11PartKey]
    );
  const activeForm11LabelPrefix = activeForm11DepreciationTarget?.type === "purchase"
    ? HAKLAI_EXPENSE_LABELS.purchaseForm11Depreciation
    : HAKLAI_EXPENSE_LABELS.form11Depreciation;
  const activeForm11DisplayLabel = activeForm11Part
    ? `${activeForm11LabelPrefix} - ${assetGroupTitleByPartKey[activeForm11PartKey] || activeForm11Part.label || ""}`
    : activeForm11LabelPrefix;
  const activeForm11Currency = activeForm11DepreciationTarget?.type === "purchase"
    ? purchaseCurrencyValue
    : "newShekel";

  return (
      <Box ref={sectionRootRef} className={`${classes.section} haklai-expenses`}>
      <Box className={classes.sectionHeader}>
        <ButtonBase className={classes.sectionHeaderButton} onClick={props.onToggleExpanded}>
          <Typography className={classes.sectionHeaderTitle}>{HAKLAI_EXPENSE_LABELS.sectionTitle}</Typography>
          <ExpandMoreIcon
            className={[
              classes.sectionToggleIcon,
              props.expanded ? classes.sectionToggleIconExpanded : ""
            ].filter(Boolean).join(" ")}
          />
        </ButtonBase>
      </Box>
      <Collapse
        in={props.expanded}
        timeout={EXPENSES_SECTION_TRANSITION_MS}
        unmountOnExit={false}
        onEnter={() => setStickyHeaderEnabled(false)}
        onEntered={() => setStickyHeaderEnabled(true)}
        onExit={() => setStickyHeaderEnabled(false)}
      >
        <Box className={classes.sectionContent}>
          <HaklaiLockNotice
            classes={classes}
            message={props.locked ? props.lockNotice?.message : ""}
          />
          <Box
            ref={lockableContentRef}
            className={props.locked ? classes.lockedSectionContent : ""}
            inert={props.locked ? "" : undefined}
          >
            <Grid container direction="column" spacing={2} alignItems="center">
              {/*
                The grid item stays stretched so the fit hook can measure the
                section. The table card inside keeps its column width.
              */}
              <Grid item style={{ alignSelf: "stretch", minWidth: 0, maxWidth: "100%", width: "100%", overflow: "hidden" }}>
                <Box
                  ref={tableFrameRef}
                  className={[
                    "haklai-data-table",
                    "haklai-calc-table",
                    isTableFit ? "is-fit-to-width" : "",
                    classes.tableFrameAlign
                  ].filter(Boolean).join(" ")}
                >
                  <TableContainer className={classes.tableWrapper}>
                    {/*
                      Width is the column sum. מרכיבי הנכס grows to this same
                      width. Neither table stretches to fill the section.
                    */}
                    <Table
                      size="small"
                      className={classes.tableInner}
                      style={{ width: tableWidth, minWidth: tableWidth }}
                    >
                      <TableHead
                        className={[
                          classes.stickyHeader,
                          (!stickyHeaderEnabled || isTableFit) ? classes.stickyHeaderDisabled : ""
                        ].filter(Boolean).join(" ")}
                      >
                        <TableRow>
                          <StyledHeaderCell
                            className="haklai-section-label-cell"
                            style={{
                              width: HAKLAI_SECTION_LABEL_COLUMN_WIDTH,
                              minWidth: HAKLAI_SECTION_LABEL_COLUMN_WIDTH,
                              maxWidth: HAKLAI_SECTION_LABEL_COLUMN_WIDTH
                            }}
                          />
                          <StyledHeaderCell style={columnStyle(columnWidths.type)}>
                            <Typography component="span" className={classes.headerLabelText}>
                              {HAKLAI_EXPENSE_LABELS.expenseType}
                            </Typography>
                          </StyledHeaderCell>
                          <StyledHeaderCell style={columnStyle(columnWidths.date)}>
                            <Typography component="span" className={classes.headerLabelText}>
                              {HAKLAI_EXPENSE_LABELS.expenseDate}
                            </Typography>
                          </StyledHeaderCell>
                          <StyledHeaderCell style={columnStyle(columnWidths.amount)}>
                            <Typography component="span" className={classes.headerLabelText}>
                              {HAKLAI_EXPENSE_LABELS.amount}
                            </Typography>
                          </StyledHeaderCell>
                          <StyledHeaderCell style={columnStyle(columnWidths.currency)}>
                            <Typography component="span" className={classes.headerLabelText}>
                              {HAKLAI_EXPENSE_LABELS.currency}
                            </Typography>
                          </StyledHeaderCell>
                          {showZabanColumn ? (
                            <StyledHeaderCell style={columnStyle(columnWidths.zaban)}>
                              <Typography component="span" className={classes.headerLabelText}>
                                {HAKLAI_EXPENSE_LABELS.zabanAttribution}
                              </Typography>
                            </StyledHeaderCell>
                          ) : null}
                          {showDepreciationColumns ? (
                            <StyledHeaderCell style={columnStyle(columnWidths.depreciationResult)}>
                              <Typography component="span" className={classes.headerLabelText}>
                                {HAKLAI_EXPENSE_LABELS.calculatedDepreciation}
                              </Typography>
                            </StyledHeaderCell>
                          ) : null}
                          {showDepreciationColumns ? (
                            <StyledHeaderCell style={columnStyle(columnWidths.depreciation)}>
                              <Tooltip title={HAKLAI_EXPENSE_TOOLTIPS.chooseDepreciation}>
                                <Typography
                                  component="span"
                                  className={classes.headerLabelText}
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {HAKLAI_EXPENSE_LABELS.depreciationDeduction}
                                </Typography>
                              </Tooltip>
                            </StyledHeaderCell>
                          ) : null}
                          <StyledHeaderCell
                            className="haklai-icon-cell"
                            style={columnStyle(ACTION_COLUMN_WIDTH)}
                          />
                        </TableRow>
                      </TableHead>
                      {assetGroups.map((assetGroup) => {
                        const partExpenses = expensesByPartKey[assetGroup.partKey] || [];
                        const { headerTitle, badge } = getAssetSectionLabel(assetGroup);
                        return (
                          <TableBody key={assetGroup.id}>
                            {renderPurchaseRow(assetGroup.partKey, { headerTitle, badge })}
                            {partExpenses.map((expense) => renderExpenseRow(expense, partExpenses))}
                          </TableBody>
                        );
                      })}
	                    </Table>
	                  </TableContainer>
	                </Box>
	              </Grid>
	            </Grid>
	          </Box>
	        </Box>
	      </Collapse>
	      {activeForm11Part && (
	        activeForm11DepreciationTarget?.type === "purchase" || activeForm11Expense
	      ) ? (
	        <HaklaiPropertyDepreciationDialog
	          open
	          onClose={closeForm11DepreciationDialog}
	          part={activeForm11Part}
	          displayLabel={activeForm11DisplayLabel}
		          depreciation={activeForm11Depreciation}
		          ownershipStartDate={purchaseDepreciationStartDate}
		          soldPropertyDate={props.soldPropertyDate}
		          acquisitionCurrency={activeForm11Currency}
		          modeOverride={HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11}
	          onSave={(nextDepreciation) => {
	            if (activeForm11DepreciationTarget?.type === "purchase") {
	              props.handlePurchaseDepreciationChange?.(
	                activeForm11PartKey,
	                nextDepreciation,
	                HAKLAI_PROPERTY_DEPRECIATION_MODE.MANUAL_FORM_11
	              );
	              return;
	            }
	            saveExpenseForm11Depreciation(
	              activeForm11Expense,
	              activeForm11PartKey,
	              nextDepreciation
	            );
	          }}
	        />
	      ) : null}
	    </Box>
	  );
	}));

HaklaiExpensesSection.displayName = "HaklaiExpensesSection";

export default HaklaiExpensesSection;
